import { Mark, mergeAttributes } from "@tiptap/core";

// Normalize curly/smart apostrophes and quotes to straight equivalents
// so entity matching works regardless of which type the editor or AI uses
export const normalizeApostrophes = (str) =>
  str.replace(/[\u2018\u2019\u201B\u2032\u02BC\uFF07]/g, "'");

// Build a regex for an entity name that:
// - escapes regex special chars
// - normalizes apostrophes
// - allows optional whitespace around apostrophes (AI sometimes inserts spaces)
// - uses word boundaries for whole-word matching
export function buildEntityRegex(entityName) {
  const normalizedName = normalizeApostrophes(entityName);
  const escapedName = normalizedName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const flexibleName = escapedName.replace(/'/g, "\\s?'\\s?");
  // For labels ending with a non-word char (e.g. "Chapter 1 -"), \b fails because there is no
  // word/non-word transition after a dash or other punctuation.  Use a whitespace/end lookahead
  // instead so "Chapter 1 -" matches in "Chapter 1 - DOOM".
  const lastChar = normalizedName.slice(-1);
  const trailingBoundary = /\w/.test(lastChar) ? "\\b" : "(?=\\s|$)";
  return new RegExp(`\\b${flexibleName}${trailingBoundary}`, "gi");
}

// Find all matches of an entity name in a text string.
// Returns an array of { start, end } positions in the text.
export function findEntityMatches(entityName, text) {
  const normalizedText = normalizeApostrophes(text);
  const regex = buildEntityRegex(entityName);
  const matches = [];
  let match;
  while ((match = regex.exec(normalizedText)) !== null) {
    matches.push({ start: match.index, end: match.index + match[0].length });
  }
  return matches;
}

function getEntitySpecName(entity) {
  return typeof entity === "string" ? entity : entity?.name || "";
}

function getEntitySpecId(entity) {
  if (typeof entity === "string") {
    return null;
  }
  return entity?.entityId || entity?.id || null;
}

const VisualizeEntities = Mark.create({
  name: "visualize-entities",
  excludes: "",
  priority: 1000,

  addOptions() {
    return {
      HTMLAttributes: {},
      entities: [],
    };
  },

  addCommands() {
    return {
      setEntities: ({ entities }) => {
        return ({ _commands, tr, state }) => {
          this.options.entities = entities;

          // Remove all existing entity marks — we'll re-apply them below.
          // This is safe because the save loop is prevented by the
          // shouldClearLastInvocation guard in the onChange handler.
          state.doc.descendants((node, pos) => {
            const marks = node.marks.filter((m) => m.type === this.type);
            if (marks.length > 0) {
              tr.removeMark(pos, pos + node.nodeSize, this.type);
            }
          });

          // Sort entities by length (shortest first) to handle contained marks
          const sortedEntities = [...entities].sort((a, b) => getEntitySpecName(a).length - getEntitySpecName(b).length);

          // First, collect all text nodes and their positions to build the full document text
          const textNodes = [];
          state.doc.descendants((node, pos) => {
            if (node.isText) {
              textNodes.push({ node, pos, text: node.text });
            }
          });

          // Build the full document text and create a mapping of character positions to node positions
          let fullText = "";
          const positionMap = [];
          textNodes.forEach(({ node, pos, text }) => {
            if (!fullText.endsWith(" ") && !text.startsWith(" ")) {
              fullText += " ";
            }
            const start = fullText.length;
            fullText += text;
            positionMap.push({ start, end: fullText.length, node, pos });
          });

          // Find all matches in the full document text
          sortedEntities.forEach((entity) => {
            const entityName = getEntitySpecName(entity);
            if (!entityName) {
              return;
            }
            const matches = findEntityMatches(entityName, fullText);

            matches.forEach(({ start: matchStart, end: matchEnd }) => {
              // Find which nodes this match spans
              const startNode = positionMap.find((n) => n.start <= matchStart && n.end > matchStart);
              const endNode = positionMap.find((n) => n.start < matchEnd && n.end >= matchEnd);

              if (startNode && endNode) {
                // Calculate the actual positions in the document
                const start = startNode.pos + (matchStart - startNode.start);
                const end = endNode.pos + (matchEnd - endNode.start);
                const safeEnd = Math.min(end, endNode.pos + endNode.node.nodeSize);

                tr.addMark(start, safeEnd, this.type.create({ entityName, entityId: getEntitySpecId(entity) }));
              }
            });
          });

          return true;
        };
      },
    };
  },

  addAttributes() {
    return {
      entityName: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-entity-name"),
        renderHTML: (attributes) => {
          if (!attributes.entityName) {
            return;
          }

          return {
            "data-entity-name": attributes.entityName,
            class: "recognized-entity",
          };
        },
      },
      entityId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-entity-id"),
        renderHTML: (attributes) => {
          if (!attributes.entityId) {
            return;
          }

          return {
            "data-entity-id": attributes.entityId,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span`,
        getAttrs: (node) => {
          if (node.hasAttribute("data-entity-name") && node.classList.contains("recognized-entity")) {
            return {
              "data-entity-name": node.getAttribute("data-entity-name"),
              "data-entity-id": node.getAttribute("data-entity-id"),
              class: "recognized-entity",
            };
          } else {
            return false;
          }
        },
      },
    ];
  },

  renderHTML({ _mark, HTMLAttributes }) {
    const elem = document.createElement("span");

    // Merge attributes and add to element
    Object.entries(mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)).forEach(([attr, val]) => elem.setAttribute(attr, val));

    return elem;
  },
});

export default VisualizeEntities;
